import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, qs } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { FilterBar, inputCls, useFiltersMeta, prodottoLabel, ANNO_CORRENTE } from "@/components/Filters";
import { dateIt, eurShort, num, statoClass, statoLabel } from "@/lib/format";
import { RicercaMobile, ScrollInfinito } from "@/components/ListaMobile";

const TRI = [["", "Tutti"], ["true", "Sì"], ["false", "No"]];

export default function Pratiche() {
  const meta = useFiltersMeta();
  const [sp] = useSearchParams();
  const initial = useMemo(() => Object.fromEntries(sp.entries()), [sp]);
  const [filters, setFilters] = useState({ anno: ANNO_CORRENTE, ...initial });
  const [flags, setFlags] = useState({ cpi: "", cvt: "", nfk: "", caricata_dal_dealer: "" });
  const [search, setSearch] = useState(initial.search || "");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [accumulate, setAccumulate] = useState([]);
  const richiesta = useRef(0);
  const [dealers, setDealers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/dashboard/dealers-periodo?${qs({ anno: filters.anno, commerciale: filters.commerciale })}`)
      .then((r) => setDealers(r.data.dealers || [])).catch(() => setDealers([]));
  }, [filters.anno, filters.commerciale]);

  useEffect(() => {
    const mia = ++richiesta.current;
    const t = setTimeout(() => {
      api.get(`/pratiche?${qs({ ...filters, ...flags, search, page, page_size: 50 })}`)
        .then((r) => {
          if (mia !== richiesta.current) return;
          setData(r.data);
          setAccumulate((prec) => (page === 1 ? r.data.rows : [...prec, ...r.data.rows]));
        })
        .catch(() => setData(null));
    }, 300);
    return () => clearTimeout(t);
  }, [filters, flags, search, page]);

  const pages = data ? Math.ceil(data.total / data.page_size) : 1;

  return (
    <div data-testid="pratiche-page">
      <SectionTitle sub={data ? `${num(data.total)} pratiche trovate · paginazione server-side` : "Caricamento…"}>
        Pratiche
      </SectionTitle>
      <FilterBar meta={meta} value={filters} onChange={(v) => { setFilters(v); setPage(1); }} dealers={dealers}
        fields={["anno", "mese", "intervallo", "commerciale", "settore", "prodotto", "macro", "stato", "canale", "dealer"]}
        extra={
          <>
            <input className={inputCls} placeholder="Cerca numero pratica, dealer, cliente…" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="pratiche-search" />
            {["cpi", "cvt", "nfk", "caricata_dal_dealer"].map((k) => (
              <select key={k} className={inputCls} value={flags[k]} data-testid={`filter-${k}`}
                onChange={(e) => { setFlags({ ...flags, [k]: e.target.value }); setPage(1); }}>
                {TRI.map(([v, l]) => <option key={v} value={v}>{k.toUpperCase().replace(/_/g, " ")}: {l}</option>)}
              </select>
            ))}
            {filters.accettate_da_liquidare === "true" && (
              <span className="text-xs bg-amber-50 border border-amber-200 text-amber-700 px-2 py-1 rounded" data-testid="badge-accettate">
                Solo accettate da liquidare
              </span>
            )}
          </>
        } />

      <RicercaMobile valore={search} testId="pratiche-search-mobile"
        placeholder="Cerca numero pratica, dealer, cliente…"
        onChange={(v) => { setSearch(v); setPage(1); }} />

      <div className="sm:hidden space-y-3 mt-3" data-testid="pratiche-cards">
        {accumulate.map((p) => (
          <Card key={p.external_id} data-testid={`pratica-card-${p.external_id}`}
            onClick={() => navigate(`/pratiche/${p.external_id}`)}
            className="p-4 active:bg-slate-50 transition-colors duration-150 cursor-pointer">
            <div className="flex items-start gap-2">
              <div className="min-w-0">
                <div className="font-mono text-sm">{p.numero_pratica}</div>
                <div className="text-sm font-medium truncate">{p.dealer_nome || p.dealer_codice}</div>
              </div>
              <span className={`ml-auto shrink-0 text-[10px] border rounded px-2 py-0.5 ${statoClass(p.stato_norm)}`}>
                {statoLabel(p.stato_norm)}
              </span>
            </div>
            <div className="mt-3 flex items-end justify-between gap-2">
              <div className="text-xs text-slate-500">
                <div>{dateIt(p.data_caricamento)} · {p.settore}</div>
                <div className="truncate">{p.commerciale_nome_raw || "—"} · {prodottoLabel(meta, p.prodotto)}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-base font-semibold">{eurShort(p.importo_finanziato)}</div>
                <div className="text-[10px] font-mono text-slate-400">
                  {[p.cpi && "CPI", p.cvt && "CVT", p.nfk && "NFK"].filter(Boolean).join(" ") || "—"}
                </div>
              </div>
            </div>
          </Card>
        ))}
        {data && !accumulate.length && (
          <Card className="p-8 text-center text-sm text-slate-500">Nessuna pratica corrisponde ai filtri.</Card>
        )}
        {!!accumulate.length && (
          <ScrollInfinito attivo={page < pages} caricando={!data}
            onAltro={() => setPage((v) => (v < pages ? v + 1 : v))} />
        )}
      </div>

      <Card className="hidden sm:block overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm" data-testid="pratiche-table">
            <thead><tr className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
              <th className="text-left px-4 py-3">N° pratica</th>
              <th className="text-left px-4 py-3">Data caric.</th>
              <th className="text-left px-4 py-3">Dealer</th>
              <th className="text-left px-4 py-3">Commerciale</th>
              <th className="text-left px-4 py-3">Canale</th>
              <th className="text-left px-4 py-3">Settore</th>
              <th className="text-left px-4 py-3">Prod.</th>
              <th className="text-left px-4 py-3">Stato</th>
              <th className="text-right px-4 py-3">Importo</th>
              <th className="text-center px-4 py-3">CPI/CVT/NFK</th>
            </tr></thead>
            <tbody>
              {(data?.rows || []).map((p) => (
                <tr key={p.external_id} data-testid={`pratica-row-${p.external_id}`}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors duration-200"
                  onClick={() => navigate(`/pratiche/${p.external_id}`)}>
                  <td className="px-4 py-3 font-mono">{p.numero_pratica}</td>
                  <td className="px-4 py-3 font-mono text-slate-500">{dateIt(p.data_caricamento)}</td>
                  <td className="px-4 py-3">{p.dealer_nome || p.dealer_codice}</td>
                  <td className="px-4 py-3 text-slate-600">{p.commerciale_nome_raw || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{p.canale}</td>
                  <td className="px-4 py-3">{p.settore}</td>
                  <td className="px-4 py-3 font-mono text-slate-500">{prodottoLabel(meta, p.prodotto)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs border rounded px-2 py-0.5 ${statoClass(p.stato_norm)}`}>{statoLabel(p.stato_norm)}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{eurShort(p.importo_finanziato)}</td>
                  <td className="px-4 py-3 text-center text-xs font-mono text-slate-500">
                    {[p.cpi && "CPI", p.cvt && "CVT", p.nfk && "NFK"].filter(Boolean).join(" ") || "—"}
                  </td>
                </tr>
              ))}
              {data && data.rows.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500">Nessuna pratica corrisponde ai filtri.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm">
          <span className="text-slate-500">Pagina {page} di {pages || 1}</span>
          <div className="flex gap-2">
            <button className="border border-slate-200 rounded px-3 py-1 disabled:opacity-40 hover:bg-slate-50 transition-colors duration-200"
              disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev">Precedente</button>
            <button className="border border-slate-200 rounded px-3 py-1 disabled:opacity-40 hover:bg-slate-50 transition-colors duration-200"
              disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="page-next">Successiva</button>
          </div>
        </div>
      </Card>
    </div>
  );
}
